import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  Building2, 
  Phone, 
  Save, 
  Clock,
  Share2,
  Sparkles
} from 'lucide-react';
import './FormsUnified.css';

const DEFAULT_FORM_DATA = {
  campus_1_label: 'Campus 1',
  campus_1_address: 'Jl. Pos Pengumben Lama No.13, Srengseng, Kec. Kembangan, Kota Jakarta Barat',
  campus_1_maps: 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9',
  campus_2_label: 'Campus 2',
  campus_2_address: 'Permata Harapan Secondary, Komplek Pos Pengumben, Jakarta Barat',
  campus_2_maps: 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9',
  phone_main: '+62 21 5890 2360',
  phone_secondary: '+62 21 5890 2361',
  whatsapp_official: '+62 812 9000 1234',
  admissions_email: 'admissions@permataharapansecondary.sch.id',
  info_email: 'info@permataharapansecondary.sch.id',
  careers_email: 'careers@permataharapansecondary.sch.id',
  instagram_url: 'https://instagram.com/ph_secondary',
  facebook_url: 'https://facebook.com/permataharapansecondary',
  youtube_url: 'https://youtube.com/@permataharapansecondary',
  linkedin_url: 'https://linkedin.com/company/permataharapansecondary',
  copyright_text: 'Permata Harapan Secondary School. All Rights Reserved.',
  brand_tagline: 'Inspiring Excellence, Nurturing Character'
};

const FIELD_LABELS = {
  campus_1_label: 'Campus 1 Label',
  campus_1_address: 'Campus 1 Address',
  campus_1_maps: 'Campus 1 Maps Link',
  campus_2_label: 'Campus 2 Label',
  campus_2_address: 'Campus 2 Address',
  campus_2_maps: 'Campus 2 Maps Link',
  phone_main: 'Phone Main',
  phone_secondary: 'Phone Secondary',
  whatsapp_official: 'WhatsApp Official',
  admissions_email: 'Admissions Email',
  info_email: 'General Info Email',
  careers_email: 'Careers Email',
  instagram_url: 'Instagram URL',
  facebook_url: 'Facebook URL',
  youtube_url: 'YouTube URL',
  linkedin_url: 'LinkedIn URL',
  copyright_text: 'Footer Copyright Text',
  brand_tagline: 'Brand Tagline'
};

export default function FooterForm({ apiBase = 'https://permataharapansecondary.sch.id/api' }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [originalData, setOriginalData] = useState(DEFAULT_FORM_DATA);
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [sectionId, setSectionId] = useState(null);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const loadFooterData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=homepage`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'footer' || s.section_type === 'site-footer');
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let rawContent = foundSection.content_published || foundSection.content;
        if (typeof rawContent === 'string') {
          try {
            rawContent = JSON.parse(rawContent);
          } catch (e) {
            rawContent = {};
          }
        }

        const dataEn = rawContent?.en || rawContent || {};
        const merged = { ...DEFAULT_FORM_DATA };
        Object.keys(DEFAULT_FORM_DATA).forEach(key => {
          if (dataEn[key] !== undefined && dataEn[key] !== null) {
            merged[key] = dataEn[key];
          }
        });

        setFormData(merged);
        setOriginalData(merged);

        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = {};
              pendJson.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  pendMap[c.field_id] = true;
                }
              });
              setPendingFields(pendMap);
            }
          }
        } catch (pe) {
          console.warn('[FooterForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[FooterForm] Load error:', err);
      toast.error('Gagal memuat konfigurasi Footer.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadFooterData();
  }, [loadFooterData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const submitSingleField = async (fieldKey) => {
    if (!sectionId) {
      toast.error('Section ID Footer tidak ditemukan di database.');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldKey,
          field_name: FIELD_LABELS[fieldKey] || fieldKey,
          old_value: originalData[fieldKey] ?? '',
          new_value: formData[fieldKey] ?? '',
          locale: 'en'
        })
      });

      const json = await res.json();
      if (json.success || json.ok) {
        toast.success(`Perubahan ${FIELD_LABELS[fieldKey] || fieldKey} berhasil diajukan untuk persetujuan Principal.`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
        setOriginalData(prev => ({ ...prev, [fieldKey]: formData[fieldKey] }));
      } else {
        toast.error(json.message || json.error || 'Gagal mengajukan perubahan');
      }
    } catch (e) {
      console.error('[FooterForm] Submit error:', e);
      toast.error('Terjadi kesalahan saat mengajukan perubahan');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const submitAllFields = async () => {
    if (!sectionId) {
      toast.error('Section ID Footer tidak ditemukan di database.');
      return;
    }

    const changedKeys = Object.keys(formData).filter(k => formData[k] !== originalData[k]);
    if (changedKeys.length === 0) {
      toast.info('Tidak ada perubahan kolom untuk disimpan.');
      return;
    }

    setSubmitting(prev => ({ ...prev, all: true }));
    let successCount = 0;

    for (const key of changedKeys) {
      try {
        const res = await fetch(`${apiBase}/cms/field-change/submit`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: key,
            field_name: FIELD_LABELS[key] || key,
            old_value: originalData[key] ?? '',
            new_value: formData[key] ?? '',
            locale: 'en'
          })
        });
        const json = await res.json();
        if (json.success || json.ok) {
          successCount++;
          setPendingFields(prev => ({ ...prev, [key]: true }));
          setOriginalData(prev => ({ ...prev, [key]: formData[key] }));
        }
      } catch (e) {
        console.error(`[FooterForm] Failed submitting ${key}:`, e);
      }
    }

    setSubmitting(prev => ({ ...prev, all: false }));

    if (successCount > 0) {
      toast.success(`${successCount} kolom berhasil diajukan untuk persetujuan Principal.`);
    } else {
      toast.error('Gagal mengajukan perubahan kolom');
    }
  };

  const renderField = (fieldKey, label, isTextarea = false, placeholder = '') => {
    const isPending = pendingFields[fieldKey];
    const isSubmitting = submitting[fieldKey];
    const hasChanged = formData[fieldKey] !== originalData[fieldKey];

    return (
      <div className={`form-group ${isTextarea ? 'full-width' : ''}`}>
        <label>{label}</label>
        <div className="input-with-action">
          {isTextarea ? (
            <textarea
              rows={2}
              value={formData[fieldKey]}
              onChange={e => handleChange(fieldKey, e.target.value)}
              placeholder={placeholder}
              disabled={isPending}
            />
          ) : (
            <input
              type="text"
              value={formData[fieldKey]}
              onChange={e => handleChange(fieldKey, e.target.value)}
              placeholder={placeholder}
              disabled={isPending}
            />
          )}
          <button
            type="button"
            className={`btn-field-save ${isTextarea ? 'textarea-save-btn' : ''}`}
            onClick={() => submitSingleField(fieldKey)}
            disabled={isPending || isSubmitting || !hasChanged}
            title={hasChanged ? 'Simpan perubahan kolom ini' : 'Belum ada perubahan'}
          >
            {isPending ? <Clock size={14} /> : <Save size={14} />}
            <span>{isSubmitting ? '...' : (isPending ? 'Pending' : 'Simpan')}</span>
          </button>
        </div>
        {isPending && <span className="field-pending-tag">Menunggu Persetujuan</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Memuat data Footer Settings...</p>
      </div>
    );
  }

  const hasAnyChanges = Object.keys(formData).some(k => formData[k] !== originalData[k]);

  return (
    <div className="unified-form-wrapper">
      <ToastContainer />

      {/* Top Banner */}
      <div className="unified-header-card">
        <div className="header-info">
          <h2>Footer Settings</h2>
          <p>Kelola informasi alamat kampus, kontak resmi, media sosial, dan identitas sekolah pada footer website.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="unified-btn-primary"
            onClick={submitAllFields}
            disabled={submitting.all || !hasAnyChanges}
          >
            <Save size={16} />
            <span>{submitting.all ? 'Menyimpan...' : 'Simpan Semua Perubahan'}</span>
          </button>
        </div>
      </div>

      {/* Campus 1 */}
      <div className="unified-section-card">
        <div className="card-header">
          <div className="title-with-icon">
            <Building2 size={20} />
            <h3>Campus 1 Details</h3>
          </div>
        </div>
        <div className="card-body form-grid-2">
          {renderField('campus_1_label', 'Campus 1 Label', false, 'Campus 1')}
          {renderField('campus_1_maps', 'Google Maps Link', false, 'https://maps.google.com/...')}
          {renderField('campus_1_address', 'Campus 1 Full Address', true, 'Alamat lengkap Kampus 1...')}
        </div>
      </div>

      {/* Campus 2 */}
      <div className="unified-section-card">
        <div className="card-header">
          <div className="title-with-icon">
            <Building2 size={20} />
            <h3>Campus 2 Details</h3>
          </div>
        </div>
        <div className="card-body form-grid-2">
          {renderField('campus_2_label', 'Campus 2 Label', false, 'Campus 2')}
          {renderField('campus_2_maps', 'Google Maps Link', false, 'https://maps.google.com/...')}
          {renderField('campus_2_address', 'Campus 2 Full Address', true, 'Alamat lengkap Kampus 2...')}
        </div>
      </div>

      {/* Official Contacts */}
      <div className="unified-section-card">
        <div className="card-header">
          <div className="title-with-icon">
            <Phone size={20} />
            <h3>Official School Contacts</h3>
          </div>
        </div>
        <div className="card-body form-grid-2">
          {renderField('phone_main', 'Primary Telephone', false, '+62 21 5890 2360')}
          {renderField('phone_secondary', 'Secondary Telephone', false, '+62 21 5890 2361')}
          {renderField('whatsapp_official', 'Official WhatsApp Number', false, '+62 812 9000 1234')}
          {renderField('admissions_email', 'Admissions Email', false, 'admissions@permataharapansecondary.sch.id')}
          {renderField('info_email', 'General Inquiries Email', false, 'info@permataharapansecondary.sch.id')}
          {renderField('careers_email', 'Careers & HR Email', false, 'careers@permataharapansecondary.sch.id')}
        </div>
      </div>

      {/* Social Media Links */}
      <div className="unified-section-card">
        <div className="card-header">
          <div className="title-with-icon">
            <Share2 size={20} />
            <h3>Social Media Channels</h3>
          </div>
        </div>
        <div className="card-body form-grid-2">
          {renderField('instagram_url', 'Instagram Profile Link', false, 'https://instagram.com/...')}
          {renderField('facebook_url', 'Facebook Page Link', false, 'https://facebook.com/...')}
          {renderField('youtube_url', 'YouTube Channel Link', false, 'https://youtube.com/...')}
          {renderField('linkedin_url', 'LinkedIn Page Link', false, 'https://linkedin.com/...')}
        </div>
      </div>

      {/* Brand & Identity */}
      <div className="unified-section-card">
        <div className="card-header">
          <div className="title-with-icon">
            <Sparkles size={20} />
            <h3>Brand & Tagline</h3>
          </div>
        </div>
        <div className="card-body form-grid-2">
          {renderField('copyright_text', 'Copyright Notice Text', false, 'Permata Harapan Secondary School. All Rights Reserved.')}
          {renderField('brand_tagline', 'School Tagline / Motto', false, 'Inspiring Excellence, Nurturing Character')}
        </div>
      </div>
    </div>
  );
}
